import { supabase } from '@/integrations/supabase/client';
import { getDeviceId } from '@/utils/device';
import logger from '@/utils/logger';
import { UserDevice } from '@/types';
import { APP_VERSION } from '@/version';
import { networkService } from '@/services/network.service';

const DEVICE_VERSION_SYNC_KEY = 'deviceAppVersionSync';
export const DEVICE_APP_VERSION_SYNC_STORAGE_KEY = DEVICE_VERSION_SYNC_KEY;
const DEVICE_VERSION_SYNC_INTERVAL_MS = 24 * 60 * 60 * 1000;

interface DeviceVersionSyncState {
  version: string;
  syncedAt: number;
}

function readDeviceVersionSyncState(): DeviceVersionSyncState | null {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(DEVICE_VERSION_SYNC_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof parsed.version === 'string' &&
      typeof parsed.syncedAt === 'number'
    ) {
      return parsed as DeviceVersionSyncState;
    }
  } catch (error) {
    logger.logError('device.service', 'readDeviceVersionSyncState', {
      additionalInfo: error
    });
  }
  return null;
}

function persistDeviceVersionSyncState(state: DeviceVersionSyncState): void {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }
  try {
    window.localStorage.setItem(DEVICE_VERSION_SYNC_KEY, JSON.stringify(state));
  } catch (error) {
    logger.logError('device.service', 'persistDeviceVersionSyncState', {
      additionalInfo: error
    });
  }
}

function setDeviceVersionSynced(timestamp = Date.now()): void {
  persistDeviceVersionSyncState({ version: APP_VERSION, syncedAt: timestamp });
}

export function clearDeviceAppVersionSyncState(): void {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }
  try {
    window.localStorage.removeItem(DEVICE_VERSION_SYNC_KEY);
  } catch (error) {
    logger.logError('device.service', 'clearDeviceAppVersionSyncState', {
      additionalInfo: error
    });
  }
}

/**
 * Registra il dispositivo corrente su Supabase tramite la funzione RPC `register_device`.
 * Se l'utente gratuito ha superato il limite di dispositivi, viene lanciato un errore.
 */
export async function registerDevice(): Promise<void> {
  const id = getDeviceId();
  const info = { ua: navigator.userAgent, platform: navigator.platform };
  const { error } = await supabase.rpc('register_device', {
    p_device_id: id,
    p_info: info,
    p_app_version: APP_VERSION
  });

  if (error) {
    logger.logError("device.service","registerDevice",{additionalInfo:error});
    if (error.message && error.message.includes('DEVICE_LIMIT_REACHED')) {
      let limit: number | undefined;
      let isPremium: boolean | undefined;
      const details = (error as any).details;
      if (details) {
        try {
          const obj = typeof details === 'string' ? JSON.parse(details) : details;
          limit = obj?.limit ?? obj?.maxDevices ?? obj?.max_devices;
          isPremium = obj?.isPremium ?? obj?.is_premium ?? obj?.premium;
        } catch {
          // ignora errori di parsing
        }
      }
      const limitText = limit ? `di ${limit} ` : '';
      const planText =
        isPremium === undefined ? '' : isPremium ? ' premium' : ' gratuito';
      throw new Error(
        `Il dispositivo non può essere registrato: limite ${limitText}raggiunto per il tuo piano${planText}.`
      );
    }
    throw error;
  }

  setDeviceVersionSynced();
  localStorage.deviceRegisteredAt = Date.now().toString();
  window.dispatchEvent(
    new CustomEvent('deviceRegistered', { detail: { ts: Date.now() } })
  );
}

interface SyncOptions {
  force?: boolean;
}

export async function syncDeviceAppVersion(options: SyncOptions = {}): Promise<boolean> {
  const { force = false } = options;
  const now = Date.now();
  const state = readDeviceVersionSyncState();
  const versionChanged = !state || state.version !== APP_VERSION;
  const expired = !state || now - state.syncedAt >= DEVICE_VERSION_SYNC_INTERVAL_MS;

  if (!force && !versionChanged && !expired) {
    return false;
  }

  if (!networkService.isOnline()) {
    logger.log('device.service', 'syncDeviceAppVersion skipped (offline)');
    return false;
  }

  const { error } = await supabase
    .from('user_devices')
    .update({
      app_version: APP_VERSION,
      updated_at: new Date().toISOString()
    })
    .eq('device_id', getDeviceId());

  if (error) {
    logger.logError('device.service', 'syncDeviceAppVersion', {
      additionalInfo: error
    });
    return false;
  }

  setDeviceVersionSynced(now);
  return true;
}

/**
 * Recupera la lista dei dispositivi associati all'utente corrente.
 */
export async function fetchDevices(): Promise<UserDevice[]> {
  const { data, error } = await supabase
    .from('user_devices')
    .select('id, device_id, device_info, last_login, created_at, app_version')
    .order('last_login', { ascending: false });

  if (error) {
    logger.logError("device.service","fetchDevices",{additionalInfo:error});
    throw error;
  }

  return (
    data?.map((d: any) => ({
      id: d.id,
      deviceId: d.device_id,
      deviceInfo: d.device_info,
      lastLogin: d.last_login,
      createdAt: d.created_at,
      appVersion: d.app_version
    })) || []
  );
}

/**
 * Rimuove un dispositivo dall'account dell'utente.
 */
export async function removeDevice(deviceId: string): Promise<void> {
  const { error } = await supabase
    .from('user_devices')
    .delete()
    .eq('device_id', deviceId);

  if (error) {
    logger.logError("device.service","removeDevice",{additionalInfo:error});
    throw error;
  }
}

export async function fetchCurrentDevice(userId: string): Promise<UserDevice | null> {
  try {
    const { data, error } = await supabase
      .from('user_devices')
      .select('id, device_id, device_info, last_login, created_at, app_version')
      .eq('user_id', userId)
      .eq('device_id', getDeviceId())
      .maybeSingle();

    if (error) {
      throw error;
    }

    if (!data) {
      return null;
    }

    return {
      id: data.id,
      deviceId: data.device_id,
      deviceInfo: data.device_info,
      lastLogin: data.last_login,
      createdAt: data.created_at,
      appVersion: data.app_version,
    };
  } catch (error) {
    logger.logError('device.service','fetchCurrentDevice',{additionalInfo:error});
    return null;
  }
}
